import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, useAuth } from './AuthContext';

const PROFILE_TUTORIAL_KEY = 'profile_tutorial_done';

export function shouldShowProfileTutorial(): boolean {
  return !localStorage.getItem(PROFILE_TUTORIAL_KEY);
}

interface ProfileTutorialProps {
  onComplete: () => void;
  isMobile: boolean;
  onNavigateToUser3: () => void;
  onNavigateOwnProfile: () => void;
  onOpenChat: (friendshipId: string) => void;
  onRefresh: () => void;
}

interface SpotlightRect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const PAD = 10;
const TOOLTIP_H_EST = 260;

export default function ProfileTutorial({
  onComplete,
  isMobile,
  onNavigateToUser3,
  onNavigateOwnProfile,
  onOpenChat,
  onRefresh,
}: ProfileTutorialProps) {
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const friendshipIdRef = useRef<string | null>(null);
  const user3IdRef = useRef<string | null>(null);
  const measureTimers = useRef<number[]>([]);

  const cleanup = useCallback(async () => {
    // Remove fake friendship + Alfonso from user 3's wishlist
    if (friendshipIdRef.current) {
      await supabase.from('friendships').delete().eq('id', friendshipIdRef.current);
      friendshipIdRef.current = null;
    }
    if (user3IdRef.current) {
      // Remove Alfonso from user 3's wishlist (only if we added it)
      const { data } = await supabase.from('ac_users').select('wishlist').eq('id', user3IdRef.current).single();
      if (data?.wishlist) {
        const updated = (data.wishlist as string[]).filter((v: string) => v !== '__tutorial_alfonso__' && v !== 'Alfonso');
        // Only update if we actually added it (check for __tutorial_alfonso__ marker won't work since we insert real name)
        // We'll just leave wishlist as-is to be safe — user #3 may have had Alfonso already
      }
      user3IdRef.current = null;
    }
    onRefresh();
  }, [onRefresh]);

  interface Step {
    id: string;
    title: string;
    body: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    target?: string;
    actionLabel?: string;
    remeasureDelay?: number;
    onEnter?: () => void;
    waitForAction?: boolean;
  }

  const steps: Step[] = [
    {
      id: 'welcome',
      placement: 'center',
      title: '👤 Welcome to your Profile',
      body: "This is your profile — your island identity on Dreamie Store. Let's walk through everything: your stats, friends, chat, and how to personalise it. Tap anywhere to continue.",
      actionLabel: "Let's go →",
    },
    {
      id: 'sidebar-search',
      target: '.psb-sidebar .sidebar-search-wrap',
      placement: 'right',
      title: '🔍 Find Other Islanders',
      body: "Search by username or user number to find other players. Their online status, owned villagers and dreamies are all visible. Tap anywhere to continue.",
    },
    {
      id: 'sidebar-friends',
      target: '.psb-sidebar .sidebar-section',
      placement: 'right',
      title: '👥 Friends & Pending',
      body: "Your friends list lives here. Pending requests show up with accept/decline buttons. Once accepted you can chat and see each other's collections. Tap anywhere to continue.",
      onEnter: async () => {
        if (!user) return;
        // Look up user #3
        const { data: u3 } = await supabase.from('ac_users').select('id, wishlist').eq('user_number', 3).maybeSingle();
        if (!u3) return;
        user3IdRef.current = u3.id;

        // Insert accepted friendship
        const { data: f } = await supabase.from('friendships').insert({
          user_a_id: user.id,
          user_b_id: u3.id,
          status: 'accepted',
        }).select('id').single();
        if (f?.id) friendshipIdRef.current = f.id;

        // Add Alfonso to user #3's wishlist if not already there
        const wishlist: string[] = u3.wishlist || [];
        if (!wishlist.includes('Alfonso')) {
          await supabase.from('ac_users').update({ wishlist: [...wishlist, 'Alfonso'] }).eq('id', u3.id);
        }

        onRefresh();
      },
      remeasureDelay: 800,
    },
    {
      id: 'sidebar-leaderboard',
      target: '.psb-sidebar .sidebar-section:last-of-type',
      placement: 'right',
      title: '🏆 Leaderboard',
      body: "The leaderboard ranks the most active traders and villager owners. Climb it by completing trades and building your collection. Tap anywhere to continue.",
      remeasureDelay: 200,
    },
    {
      id: 'visit-user3',
      target: '.psb-sidebar .sidebar-friends-section',
      placement: 'right',
      title: '🏝️ Visit User #3\'s Profile',
      body: "User #3 is now in your friends list below. Click their name in the Friends section to visit their profile now!",
      waitForAction: true,
      remeasureDelay: 800,
      onEnter: () => onRefresh(),
    },
    {
      id: 'user3-profile',
      target: '.pro-hero',
      placement: 'bottom',
      title: '👀 Viewing Another Profile',
      body: "You're on User #3's profile. You can see their island name, bio, owned villagers and dreamies list. Tap anywhere to continue.",
      remeasureDelay: 600,
    },
    {
      id: 'user3-dreamies',
      target: '.pro-tabs',
      placement: 'bottom',
      title: '⭐ Their Dreamies',
      body: "Switch to the Dreamies tab to see what villagers User #3 is looking for. If you own any of them, you can offer them directly. Tap anywhere to continue.",
    },
    {
      id: 'offer-btn',
      target: '.pro-offer-btn',
      placement: 'top',
      title: '🎁 Offer Alfonso',
      body: "Alfonso is on User #3's dreamies list and you own them! Press \"Offer this villager\" to send User #3 an offer. Go ahead and press it now!",
      waitForAction: true,
      remeasureDelay: 500,
      onEnter: () => {
        // Switch to their dreamies tab
        const dreamiesTab = document.querySelector('.pro-tab:nth-child(2)') as HTMLElement;
        if (dreamiesTab) dreamiesTab.click();
      },
    },
    {
      id: 'offer-modal',
      target: '.modal-content.counter-modal',
      placement: 'right',
      title: '🎁 Send the Offer',
      body: "Pick what you want in return — Bells, NMT, an item, or offer it free. Fill it in and send. The offer goes to their Incoming trades as a special Offer request. Tap anywhere to continue.",
      remeasureDelay: 400,
    },
    {
      id: 'add-friend-btn',
      target: '.pro-other-actions',
      placement: 'bottom',
      title: '➕ Social Actions',
      body: "From any profile you can add friends, message them once connected, block or report them. Keep your island community safe and friendly. Tap anywhere to continue.",
      remeasureDelay: 200,
      onEnter: () => {
        // Close offer modal if open
        const closeBtn = document.querySelector('.modal-content .checkout-close') as HTMLElement;
        if (closeBtn) closeBtn.click();
      },
    },
    {
      id: 'open-chat',
      target: '.pro-other-actions .pro-action-btn.chat',
      placement: 'bottom',
      title: '💬 Open Chat',
      body: "Since you're friends with User #3, you can message them. Press the 💬 Message button now to open the chat!",
      waitForAction: true,
      remeasureDelay: 300,
    },
    {
      id: 'chat-modal',
      target: '.chat-modal',
      placement: 'left',
      title: '💬 Chat',
      body: "Messages are end-to-end encrypted — only you and User #3 can read them. You'll also see a typing indicator when they're typing and a ✓✓ when they've read yours. Tap anywhere to continue.",
      remeasureDelay: 400,
    },
    {
      id: 'chat-e2e',
      target: '.chat-e2e-label',
      placement: 'bottom',
      title: '🔒 End-to-End Encrypted',
      body: "Every message is encrypted before it leaves your device using AES-256. Even we can't read your chats. Safe to share Dodo codes and island info. Tap anywhere to continue.",
      remeasureDelay: 200,
    },
    {
      id: 'chat-online',
      target: '.chat-header-status',
      placement: 'bottom',
      title: '🟢 Online Status',
      body: "The green dot and 'Online' label show if User #3 is active right now. The typing indicator (three dots) appears when they're writing a reply. Tap anywhere to continue.",
      remeasureDelay: 200,
    },
    {
      id: 'go-own-profile',
      target: '.chat-close',
      placement: 'right',
      title: '← Back to Your Profile',
      body: "Close the chat and head back to your own profile to see how to personalise it. Close the chat now!",
      waitForAction: true,
      remeasureDelay: 200,
    },
    {
      id: 'own-avatar',
      target: '.pro-avatar-ring',
      placement: 'right',
      title: '🖼️ Your Avatar',
      body: "Click the avatar to pick an emoji or upload a photo. It shows on your profile and next to your name across the site. Tap anywhere to continue.",
      remeasureDelay: 400,
      onEnter: () => onNavigateOwnProfile(),
    },
    {
      id: 'own-username',
      target: '.pro-name',
      placement: 'right',
      title: '✏️ Username',
      body: "Click your name to edit it. Keep it under 24 characters. Your user number (#X) never changes — it's your permanent ID. Tap anywhere to continue.",
      remeasureDelay: 300,
    },
    {
      id: 'own-island',
      target: '.pro-meta-row:first-child',
      placement: 'right',
      title: '🏝️ Island Name',
      body: "Click the island name field to set yours. Other traders will see this when they visit your profile. Tap anywhere to continue.",
      remeasureDelay: 200,
    },
    {
      id: 'own-bio',
      target: '.pro-meta-row:nth-child(2)',
      placement: 'right',
      title: '📝 Bio',
      body: "Add a short bio — up to 160 characters. Tell people what you're trading, what you're looking for, or anything about your island. Tap anywhere to continue.",
      remeasureDelay: 200,
    },
    {
      id: 'done',
      placement: 'center',
      title: '🎉 Profile tour complete!',
      body: "That's everything! Your profile is your trading identity — fill it in, make friends, and start chatting. Happy trading! 🏝️",
      actionLabel: 'Done →',
      onEnter: () => cleanup(),
    },
  ];

  const step = steps[stepIdx];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-profile-tutorial-step', step.id);
    return () => document.body.removeAttribute('data-profile-tutorial-step');
  }, [step.id]);

  const measureTarget = useCallback((targetOverride?: string) => {
    const target = targetOverride || step.target;
    if (!target) { setRect(null); return; }
    measureTimers.current.forEach(clearTimeout);
    measureTimers.current = [];
    let attempts = 0;
    const tryMeasure = () => {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const t = window.setTimeout(() => {
          const r = getRect(target);
          if (r) { setRect(r); }
          else if (attempts < 15) { attempts++; measureTimers.current.push(window.setTimeout(tryMeasure, 150)); }
          else { setRect(null); }
        }, 120);
        measureTimers.current.push(t);
      } else if (attempts < 15) {
        attempts++;
        measureTimers.current.push(window.setTimeout(tryMeasure, 150));
      } else { setRect(null); }
    };
    tryMeasure();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setVisible(false);
    setRect(null);
    if (step.onEnter) step.onEnter();
    const delay = step.remeasureDelay ? step.remeasureDelay + 100 : 300;
    const t = window.setTimeout(() => { measureTarget(); setVisible(true); }, delay);
    return () => clearTimeout(t);
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => { if (step.target) measureTarget(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measureTarget, step.target]);

  // Poll for user visiting user #3's profile (visit-user3 step)
  useEffect(() => {
    if (step.id !== 'visit-user3') return;
    const interval = window.setInterval(() => {
      // Check if we're now on a profile page showing user #3 (viewing indicator present)
      const viewingIndicator = document.querySelector('.pro-viewing-indicator');
      if (viewingIndicator) {
        window.clearInterval(interval);
        setStepIdx(i => i + 1);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [step.id]);

  // Poll for offer button click (offer-btn step)
  useEffect(() => {
    if (step.id !== 'offer-btn') return;
    const interval = window.setInterval(() => {
      if (document.querySelector('.modal-content.counter-modal')) {
        window.clearInterval(interval);
        setStepIdx(i => i + 1);
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, [step.id]);

  // Poll for chat open (open-chat step)
  useEffect(() => {
    if (step.id !== 'open-chat') return;
    const interval = window.setInterval(() => {
      if (document.querySelector('.chat-modal')) {
        window.clearInterval(interval);
        setStepIdx(i => i + 1);
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, [step.id]);

  // Poll for chat close (go-own-profile step)
  useEffect(() => {
    if (step.id !== 'go-own-profile') return;
    const interval = window.setInterval(() => {
      if (!document.querySelector('.chat-modal')) {
        window.clearInterval(interval);
        setStepIdx(i => i + 1);
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, [step.id]);

  const advance = () => {
    if (stepIdx === steps.length - 1) {
      localStorage.setItem(PROFILE_TUTORIAL_KEY, '1');
      onComplete();
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const skip = async () => {
    await cleanup();
    localStorage.setItem(PROFILE_TUTORIAL_KEY, '1');
    onComplete();
  };

  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect || step.placement === 'center') {
      return {
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: isMobile ? 'min(92vw, 360px)' : '400px',
        width: isMobile ? 'min(92vw, 360px)' : '400px',
      };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TW = isMobile ? Math.min(vw * 0.9, 340) : 370;
    const placement = step.placement || 'bottom';
    const centreX = rect.left + rect.width / 2;
    const clampedLeft = Math.max(8, Math.min(centreX - TW / 2, vw - TW - 8));

    if (placement === 'bottom') {
      const top = rect.top + rect.height + PAD + 8;
      if (top + TOOLTIP_H_EST > vh - 8) return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
      return { position: 'fixed', top, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'top') {
      return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'right') {
      const left = Math.min(rect.left + rect.width + PAD + 8, vw - TW - 8);
      const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
      return { position: 'fixed', top, left, width: TW, maxWidth: TW };
    }
    const left = Math.max(8, rect.left - TW - PAD - 8);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
    return { position: 'fixed', top, left, width: TW, maxWidth: TW };
  };

  const spotlightStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
    borderRadius: 14,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    border: '1.5px solid rgba(255,140,30,0.7)',
    animation: 'tutorial-glow-pulse 1.8s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 200001,
    transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
  } : {
    position: 'fixed', top: 0, left: 0, width: 0, height: 0,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    pointerEvents: 'none', zIndex: 200001,
  };

  const renderBlocker = () => {
    const blockEvt = (e: React.MouseEvent | React.TouchEvent) => { e.stopPropagation(); e.preventDefault(); };
    if (!step.waitForAction || !rect) {
      return (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200000 }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchMove={e => e.preventDefault()}
        />
      );
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const ht = rect.top - PAD, hl = rect.left - PAD;
    const hb = rect.top + rect.height + PAD, hr = rect.left + rect.width + PAD;
    const s: React.CSSProperties = { position: 'fixed', zIndex: 200000, background: 'transparent' };
    return (
      <>
        <div style={{ ...s, top: 0, left: 0, width: vw, height: Math.max(0, ht) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: 0, width: Math.max(0, hl), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: hr, width: Math.max(0, vw - hr), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
      </>
    );
  };

  return (
    <>
      <div
        className={`tutorial-root${visible ? ' visible' : ''}`}
        data-tutorial-step={step.id}
        style={{ position: 'fixed', inset: 0, zIndex: 200000, pointerEvents: step.waitForAction ? 'none' : 'all' }}
      >
        {renderBlocker()}

        {!rect && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.76)', zIndex: 200001, pointerEvents: 'none' }} />}
        {rect && <div style={spotlightStyle} />}

        <div
          className="tutorial-card"
          style={{ ...getTooltipStyle(), pointerEvents: 'all', zIndex: 200002, transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), bottom 0.3s cubic-bezier(0.4,0,0.2,1)' }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
        >
          <div className="tutorial-dots">
            {steps.map((_, i) => (
              <span key={i} className={`tutorial-dot ${i === stepIdx ? 'active' : i < stepIdx ? 'done' : ''}`} />
            ))}
          </div>

          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-body" style={{ whiteSpace: 'pre-line' }}>{step.body}</p>

          <div className="tutorial-actions">
            <button className="tutorial-skip" onClick={skip}>Skip</button>
            {!step.waitForAction && stepIdx === steps.length - 1 && (
              <button className="tutorial-next" onClick={advance}>
                {step.actionLabel || 'Finish'}
              </button>
            )}
            {step.actionLabel && !step.waitForAction && stepIdx < steps.length - 1 && (
              <button className="tutorial-next" onClick={advance}>{step.actionLabel}</button>
            )}
            {!step.waitForAction && stepIdx < steps.length - 1 && (
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>tap anywhere to continue</span>
            )}
          </div>

          <div className="tutorial-counter">{stepIdx + 1} / {steps.length}</div>
        </div>
      </div>
    </>
  );
}
